import Vue, {PluginObject} from 'vue';
import VueI18n, {IVueI18n, LocaleMessageObject} from 'vue-i18n';

Vue.use(VueI18n);

const TRANSLATION_WARNINGS: boolean = (process.env.VUE_APP_TRANSLATION_WARNINGS || 'false') === 'true';

export const i18n = new VueI18n({
  locale: process.env.VUE_APP_I18N_LOCALE || 'en-gb',
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en-gb',
  silentTranslationWarn: !TRANSLATION_WARNINGS,
  silentFallbackWarn: !TRANSLATION_WARNINGS,
});

const loadedLanguages: string[] = [];

interface Translation {
  key: string;
  value: string;
}

interface Substitutions {
  [key: string]: string;
}

/**
 * Method to manually replace :example in texts with provided substitutions
 *
 * @param text
 * @param substitutions
 * @return THe substituted text
 */
function replace(text: string, substitutions: Substitutions|null): string {
  if (substitutions) {
    for (const s in substitutions) {
      if (substitutions.hasOwnProperty(s)) {
        text = text.replace(new RegExp('{' + s + '}', 'ig'), substitutions[s]);
      }
    }
  }

  return text;
}

function iTranslate(key: string, ...args: any[]) {
  const fallback = (args.length > 0) ? args.splice(-1, 1)[0] : null;
  const substitutions = (args.length > 0) ? args[0] : null;

  return (i18n.te(key))
    ? i18n.t(key, ...args)
    : (fallback)
      ? replace(fallback, substitutions)
      : key;
}

function iTranslateC(key: string, ...args: any[]) {
  const fallback = (args.length > 0) ? args.splice(-1, 1)[0] : null;
  const substitutions = (args.length > 0) ? args[0] : null;

  return (i18n.te(key))
    ? i18n.tc(key, ...args)
    : (fallback)
      ? replace(fallback, substitutions)
      : key;
}

function iDate(dateTime: Date|string|null, ...args: any[]) {
  if (dateTime === null) {
    return null;
  }

  let dt = dateTime;
  if (typeof(dt) === 'string') {
    try {
      dt = new Date(dt);
    } catch (e) {
      return null;
    }
  }

  return i18n.d(dt, ...args);
}

function iNumber(n: number, ...args: any[]): string {
  return i18n.n(n, ...args);
}

function setI18nLanguage(lang: string, data: Translation[]): string {
  const translations: LocaleMessageObject = {};
  data.forEach((item) => {
    translations[item.key] = item.value;
  });

  i18n.setDateTimeFormat(lang.toLowerCase(), {
    dateUtc: {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Europe/Amsterdam',
    },
    dateUtcWithoutYear: {
       month: 'short', day: 'numeric', timeZone: 'Europe/London',
    },
    date: {
      year: 'numeric', month: 'short', day: 'numeric',
    },
    dateWithoutYear: {
      month: 'short', day: 'numeric',
    },
    time: {
      hour: 'numeric', minute: 'numeric',
    },
    dateTime: {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
    },
    dateTimeWithoutYear: {
      month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
    },
  });

  i18n.setNumberFormat(lang.toLowerCase(), {
    percentage: {
      maximumFractionDigits: 0,
    },
    meters: {
      maximumFractionDigits: 0,
    },
  });

  i18n.setLocaleMessage(lang.toLowerCase(), translations);
  i18n.locale = lang.toLowerCase();
  localStorage.setItem('lang', i18n.locale);
  loadedLanguages.push(lang);

  return lang;
}

export async function loadLanguage(lang: string): Promise<string> {
  if (i18n.locale !== lang) {
    if (!loadedLanguages.includes(lang)) {

      return setI18nLanguage('en', []);
    }
  }

  return lang;
}

const Translation: PluginObject<any> = {
  install: (vue: any, options: any) => {
    vue.prototype.$it = iTranslate;
    vue.prototype.$itc = iTranslateC;
    vue.prototype.$id = iDate;
    vue.prototype.$in = iNumber;
  },
};

Vue.use(Translation);

export default {
  locale: i18n.locale,
  it: iTranslate,
  itc: iTranslateC,
  id: iDate,
  ite: i18n.te,
  in: iNumber,
};


